/**
 * GeocodeCache - In-memory cache for geocoding results
 * 
 * Prevents expensive repeated API calls to Google Maps Geocoding API
 * Stores results in memory during app session
 */

interface GeocodedLocation {
    lat: number;
    lng: number;
    address: string;
    timestamp: number;
}

class GeocodeCache {
    private static cache: Map<string, GeocodedLocation> = new Map();
    
    // Cache expiration time (24 hours in milliseconds)
    private static readonly CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000;
    
    // Maximum cache size (prevent memory bloat)
    private static readonly MAX_CACHE_SIZE = 1000;

    /**
     * Get cached geocoding result
     */
    static get(address: string): GeocodedLocation | null {
        const normalized = this.normalizeAddress(address);
        const cached = this.cache.get(normalized);
        
        if (!cached) {
            return null;
        }
        
        // Check if cache entry has expired
        const now = Date.now();
        if (now - cached.timestamp > this.CACHE_EXPIRY_MS) {
            this.cache.delete(normalized);
            return null;
        }
        
        return cached;
    }

    /**
     * Store geocoding result in cache
     */
    static set(address: string, lat: number, lng: number): void {
        const normalized = this.normalizeAddress(address);
        
        // Implement LRU eviction if cache is full
        if (this.cache.size >= this.MAX_CACHE_SIZE) {
            // Remove oldest entry
            const firstKey = this.cache.keys().next().value;
            if (firstKey) {
                this.cache.delete(firstKey);
            }
        }
        
        this.cache.set(normalized, {
            lat,
            lng,
            address: normalized,
            timestamp: Date.now(),
        });
    }

    /**
     * Normalize address for consistent caching
     * Removes extra spaces, converts to lowercase
     */
    private static normalizeAddress(address: string): string {
        return address
            .toLowerCase()
            .trim()
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .replace(/,\s*/g, ', '); // Normalize comma spacing
    }

    /**
     * Clear entire cache
     */
    static clear(): void {
        this.cache.clear();
    }

    /**
     * Get cache statistics (useful for debugging)
     */
    static getStats(): { size: number; maxSize: number } {
        return {
            size: this.cache.size,
            maxSize: this.MAX_CACHE_SIZE,
        };
    }

    /**
     * Remove expired entries from cache
     */
    static cleanupExpired(): number {
        const now = Date.now();
        let removed = 0;
        
        for (const [key, value] of this.cache.entries()) {
            if (now - value.timestamp > this.CACHE_EXPIRY_MS) {
                this.cache.delete(key);
                removed++;
            }
        }
        
        return removed;
    }
}

export default GeocodeCache;

